import React, { useState } from 'react';
import { Button, TextInput, Label, Checkbox, Alert } from '@c2fo/liquidity';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../lib/apiClient';

export interface Division {
  id?: number;
  uuid: string;
  name: string;
  makerDivisionName?: string;
  organizationName?: string;
  eligibleInvoiceAmount?: number;
  buyerSupplierId?: string;
  makerOrganizationUuid?: string;
  takerId?: number;
}

interface DivisionSearchProps {
  selectedDivisions: Division[];
  onDivisionsChange: (divisions: Division[]) => void;
  required?: boolean;
  searchEndpoint?: string;
  placeholder?: string;
  showSelectedList?: boolean;
  className?: string;
}

export const DivisionSearch: React.FC<DivisionSearchProps> = ({
  selectedDivisions,
  onDivisionsChange,
  required = false,
  searchEndpoint = 'api/admin/v2/division/search',
  placeholder = 'Enter division name...',
  showSelectedList = true,
  className = '',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Division[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const searchMutation = useMutation({
    mutationFn: async (query: string) => {
      const response = await apiClient()
        .get(`${searchEndpoint}?q=${encodeURIComponent(query)}`)
        .json<Division[] | { data: Division[] } | { divisions: Division[] }>();

      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }

      if ('data' in response && Array.isArray(response.data)) {
        return response.data;
      }

      if ('divisions' in response && Array.isArray(response.divisions)) {
        return response.divisions;
      }

      return [];
    },
    onSuccess: (data) => {
      setSearchResults(data);
      setHasSearched(true);

      // Auto-select if only one result
      if (data.length === 1 && selectedDivisions.length === 0) {
        onDivisionsChange([data[0]]);
      }
    },
    onError: () => {
      // Error surfaced via searchMutation.isError and Alert in UI
    },
  });

  const handleSearch = () => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return;
    searchMutation.mutate(trimmedQuery);
  };

  const handleDivisionToggle = (division: Division, checked: boolean) => {
    if (checked) {
      onDivisionsChange([...selectedDivisions, division]);
    } else {
      onDivisionsChange(selectedDivisions.filter((d) => d.uuid !== division.uuid));
    }
  };

  const isDivisionSelected = (division: Division) => {
    return selectedDivisions.some((d) => d.uuid === division.uuid);
  };

  const handleSelectAll = () => {
    onDivisionsChange(searchResults);
  };

  const handleSelectNone = () => {
    onDivisionsChange([]);
  };

  const allSelected = searchResults.length > 0 && searchResults.every(isDivisionSelected);
  const someSelected = searchResults.some(isDivisionSelected);

  return (
    <div className={`space-y-4 w-full ${className}`}>
      <div>
        <Label htmlFor="divisionSearch" className="block mb-2">
          Search for Divisions {required && '*'}
        </Label>
        <div className="flex gap-2">
          <TextInput
            id="divisionSearch"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={placeholder}
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            loading={searchMutation.isPending}
            disabled={!searchQuery.trim() || searchMutation.isPending}
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>
      </div>

      {searchMutation.isError && (
        <Alert type="error" variant="filled" description="Failed to search divisions. Please try again." />
      )}

      {hasSearched && searchResults.length === 0 && (
        <div className="p-4 bg-gray-100 rounded text-gray-600 text-center">
          No divisions found matching your search.
        </div>
      )}

      {searchResults.length > 0 && (
        <div>
          <Label className="block mb-2">Select Divisions to Add {required && '*'}</Label>
          <div className="border border-gray-300 rounded-md overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-300 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 text-left font-medium text-gray-700 w-10">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleSelectAll();
                          } else {
                            handleSelectNone();
                          }
                        }}
                        aria-label="Select all divisions"
                      />
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">Division Name</th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      {searchEndpoint.includes('v2') ? 'Maker Division' : 'Organization'}
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">Eligible Amount</th>
                    <th className="p-3 text-left font-medium text-gray-700">Buyer Supplier ID</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((division, index) => (
                    <tr
                      key={`${division.uuid}-${index}`}
                      className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={isDivisionSelected(division)}
                          onChange={(e) => handleDivisionToggle(division, e.target.checked)}
                          aria-label={`Select ${division.name}`}
                        />
                      </td>
                      <td className="p-3">{division.name || '-'}</td>
                      <td className="p-3">{division.makerDivisionName || division.organizationName || '-'}</td>
                      <td className="p-3">
                        {division.eligibleInvoiceAmount !== undefined
                          ? `$${division.eligibleInvoiceAmount.toLocaleString()}`
                          : '-'}
                      </td>
                      <td className="p-3">{division.buyerSupplierId || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showSelectedList && selectedDivisions.length > 0 && (
        <div>
          <Label className="block mb-2">Selected Divisions ({selectedDivisions.length})</Label>
          <div className="space-y-1">
            {selectedDivisions.map((division, index) => (
              <div
                key={`selected-${division.uuid}-${index}`}
                className="flex items-center justify-between p-2 bg-gray-50 rounded"
              >
                <span className="text-sm">{division.name}</span>
                <button
                  type="button"
                  onClick={() => handleDivisionToggle(division, false)}
                  className="text-red-600 hover:text-red-700 text-sm"
                  aria-label={`Remove ${division.name}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
